/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import type {
  GetProductAvailabilitiesRequest,
  PastProductOrder,
  PriceInfo,
  ProductAvailability,
} from "@farmlands/farmlandspro-api";

export type Order = Required<PastProductOrder>;

export enum LifeCycleState {
  STOCKED = "Active- Stocked",
  NOT_STOCKED = "Active- not stocked",
  OBSOLETE = "Obsolete All sites",
  DISCONTINUED = "Discontinued Item",
  END_OF_LIFE = "End of Life",
  TEMPORARILY_UNAVAILABLE = "Temporarily Unavailable",
}

export interface Promotion {
  id: string;
  name: string;
  type: string;
}

export interface TypesensePriceBreak {
  dateFrom: string;
  qtyFrom: number;
  qtyTo: number;
  unit: string;
}

export interface ProductDetailsPriceBreaks {
  effectiveUnitDiscountPercentage: number;
  from: number;
  netUnitPriceExcGst: number;
  to: number;
  unit: string;
}

export interface Product {
  basePriceExcGst: number;
  basePriceIncGst: number;
  category: string;
  subCategory?: string;
  colour?: string;
  colourDescription?: string;
  name: string;
  description: string;
  discountedPriceExcGst: number;
  discountedPriceIncGst: number;
  editedPriceExcGst?: number;
  originalPriceExcGst?: number;
  lastEditedReasonCode?: string | null;
  lastEditedReasonNote?: string | null;
  imageUrl?: string;
  isPriceNegotiable?: boolean;
  itemNumber: string;
  lifeCycleState: string | null;
  masterProductId: number;
  orders: Order[];
  productId: number;
  size?: string;
  sizeDescription?: string;
  hasVariants?: boolean;
  alternativeProduct?: Product;
  stockAvailability?: string;
  stockAvailabilities?: StockAvailabilityType[];
  promotions?: Promotion[];
  priceBreaks?: (TypesensePriceBreak | ProductDetailsPriceBreaks)[];
}

export interface ProductsByProductIdDictionary {
  [index: string]: Product;
}

export type MediaLocationType = {
  uri: string;
  isDefault: boolean;
};

export interface Variant {
  productId: number;
  productVariantNumber: string;
  name: string;
  dimensions: Dimension[];
}

export interface Dimension {
  type: number;
  id: string;
  value: string;
  displayOrder?: number | null;
}

export interface DetailsApiResponse {
  name: string;
  brand: string;
  onlineShortDescription: string;
  onlineLongDescription: string;
  basePriceExcGst: number;
  discountedPriceExcGst: number;
  variants?: Variant[];
  mediaLocations?: MediaLocationType[];
}
export type ProductDetailsApiResponse = Omit<Product, "priceBreaks"> &
  DetailsApiResponse & {
    priceBreaks?: ProductDetailsPriceBreaks[];
  };

export interface StocksState {
  stocks: ProductsByProductIdDictionary;
}

export type StockAvailabilitiesPayload = GetProductAvailabilitiesRequest;

export type StockAvailabilityType = Required<ProductAvailability>;

export interface StockAvailabilities {
  availabilities: StockAvailabilityType[];
}

export type ActivePrice = Required<PriceInfo>;

export type ActivePrices = {
  prices: ActivePrice[];
};
